import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { Card, CardContent } from "@/components/ui/card";
import IntegrationCapabilities from "@/components/IntegrationCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  MessageSquare, KeyRound, Loader2, Save, CheckCircle2, AlertOctagon,
  Send, RefreshCw, Trash2, Phone, Wallet, Building2, Edit3, X,
  MessageCircle, Globe, Sparkles, Briefcase,
} from "lucide-react";

const STATUS_TONE = {
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  sent: "bg-sky-100 text-sky-800 border-sky-200",
  queued: "bg-amber-100 text-amber-800 border-amber-200",
  sending: "bg-amber-100 text-amber-800 border-amber-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  undelivered: "bg-rose-100 text-rose-800 border-rose-200",
};

function CredsCard({ state, setState, onSave, onTest, onClear, saving, testing }) {
  const onChange = (k, v) => setState((s) => ({ ...s, [k]: v }));
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
          <KeyRound className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">Master credentials</h2>
          {state.configured && (
            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 ml-2">
              <CheckCircle2 className="h-3 w-3 mr-1" /> configured
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="label-overline">Account SID</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={state.account_sid || ""}
              onChange={(e) => onChange("account_sid", e.target.value)}
              data-testid="twilio-account-sid" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">Auth Token</Label>
            <Input className="rounded-sm font-mono text-[12px]" type="password"
              placeholder={state.has_auth_token ? "••••••••••••••• (saved)" : "32-char hex"}
              value={state.auth_token || ""}
              onChange={(e) => onChange("auth_token", e.target.value)}
              data-testid="twilio-auth-token" />
            {state.has_auth_token && !state.auth_token && (
              <p className="text-[10px] text-zinc-500">Leave blank to keep the saved token.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">Messaging Service SID (optional)</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={state.messaging_service_sid || ""}
              onChange={(e) => onChange("messaging_service_sid", e.target.value)}
              data-testid="twilio-messaging-sid" />
            <p className="text-[10px] text-zinc-500">Picks the best From number automatically. Falls back to the field below if blank.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">From number (E.164)</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder="+61400000000"
              value={state.from_number || ""}
              onChange={(e) => onChange("from_number", e.target.value)}
              data-testid="twilio-from-number" />
          </div>
        </div>

        {state.last_verified_at && (
          <div className="text-[11px] text-zinc-500 font-mono">
            Last verified: {state.last_verified_at} · Account:{" "}
            <strong>{state.account_friendly_name || "—"}</strong>{" "}
            ({state.account_status || "—"})
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
          {state.configured && (
            <Button type="button" variant="ghost" size="sm" className="rounded-sm text-rose-600 hover:bg-rose-50"
              onClick={onClear} data-testid="twilio-clear">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear creds
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" className="rounded-sm"
            onClick={onTest} disabled={testing || !state.configured}
            data-testid="twilio-test">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
            Test connection
          </Button>
          <Button type="button" size="sm" className="rounded-sm"
            onClick={onSave} disabled={saving} data-testid="twilio-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save credentials
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RcsConfigCard({ state, setState, onSave, saving }) {
  const onChange = (k, v) => setState((s) => ({ ...s, [k]: v }));
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="twilio-rcs-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
          <Sparkles className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">RCS messaging</h2>
          {state.rcs_configured && (
            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 ml-2"
              data-testid="twilio-rcs-configured-badge">
              <CheckCircle2 className="h-3 w-3 mr-1" /> configured
            </Badge>
          )}
          {state.rcs_enabled && state.rcs_configured && (
            <Badge className="rounded-sm bg-sky-100 text-sky-800 border-sky-200">
              enabled
            </Badge>
          )}
        </div>

        <p className="text-[12px] text-zinc-600 max-w-2xl">
          RCS (Rich Communication Services) delivers verified-sender,
          branded business messages with read receipts and typing indicators
          on supported Android handsets. Configure once in the{" "}
          <a className="underline decoration-zinc-400"
            href="https://console.twilio.com" target="_blank" rel="noreferrer">Twilio Console</a>{" "}
          under <strong>Messaging → RCS → Senders</strong>, get your sender
          carrier-approved, then paste the IDs below. Twilio falls back to
          SMS automatically when the recipient device isn&apos;t RCS-capable.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="label-overline">RCS Agent ID</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder="brand_xyz_agent"
              value={state.rcs_agent_id || ""}
              onChange={(e) => onChange("rcs_agent_id", e.target.value)}
              data-testid="twilio-rcs-agent-id" />
            <p className="text-[10px] text-zinc-500">
              From console → Messaging → RCS → Senders → Settings.
              Don&apos;t include the <code>rcs:</code> prefix.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-overline">RCS Messaging Service SID</Label>
            <Input className="rounded-sm font-mono text-[12px]"
              placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={state.rcs_messaging_service_sid || ""}
              onChange={(e) => onChange("rcs_messaging_service_sid", e.target.value)}
              data-testid="twilio-rcs-messaging-sid" />
            <p className="text-[10px] text-zinc-500">
              A Messaging Service that bundles this RCS Sender with an
              SMS-enabled phone number for fallback. Can be the same
              service used for SMS above.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <label className="flex items-start gap-2 cursor-pointer"
            data-testid="twilio-rcs-enabled-label">
            <input type="checkbox"
              className="mt-1 h-4 w-4 rounded-sm accent-zinc-900"
              checked={!!state.rcs_enabled}
              onChange={(e) => onChange("rcs_enabled", e.target.checked)}
              data-testid="twilio-rcs-enabled" />
            <div>
              <div className="text-[13px] font-semibold">Enable RCS sending</div>
              <div className="text-[11px] text-zinc-500 max-w-md">
                Master switch — when off, every send goes via SMS even if
                RCS credentials are populated.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox"
              className="mt-1 h-4 w-4 rounded-sm accent-zinc-900"
              checked={state.rcs_fallback_sms !== false}
              onChange={(e) => onChange("rcs_fallback_sms", e.target.checked)}
              data-testid="twilio-rcs-fallback" />
            <div>
              <div className="text-[13px] font-semibold">Auto-fallback to SMS</div>
              <div className="text-[11px] text-zinc-500 max-w-md">
                When the recipient device doesn&apos;t support RCS, Twilio
                routes via the SMS number attached to the same Messaging
                Service. Recommended on.
              </div>
            </div>
          </label>
        </div>

        {state.rcs_enabled && !state.rcs_configured && (
          <p className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2"
            data-testid="twilio-rcs-missing-warning">
            <AlertOctagon className="h-3.5 w-3.5 inline mr-1" />
            RCS is enabled but Agent ID + Messaging Service SID aren&apos;t
            both set. Sends will fail with a configuration error until both
            fields are filled.
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
          <Button type="button" size="sm" className="rounded-sm"
            onClick={onSave} disabled={saving}
            data-testid="twilio-rcs-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save RCS settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SendTestCard({ configured, rcsConfigured, rcsEnabled, onSent }) {
  const [to, setTo] = useState("");
  const [body, setBody] = useState("Test from Iceberg Digital — your Twilio relay is working.");
  const [channel, setChannel] = useState("sms");
  const [sending, setSending] = useState(false);

  const canSendRcs = rcsConfigured && rcsEnabled;

  const send = async () => {
    if (!to.trim()) { toast.error("Enter a destination number."); return; }
    if (channel === "rcs" && !canSendRcs) {
      toast.error("Configure + enable RCS above before sending an RCS test.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/twilio/master/send-test", {
        to: to.trim(), body, channel,
      });
      toast.success(`Sent ${channel.toUpperCase()} (SID ${data.sid}) — status: ${data.status}`);
      setTo("");
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
          <Send className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">Send a test message</h2>
        </div>
        {!configured && (
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
            Save + verify credentials above before sending a test.
          </p>
        )}

        {/* Channel toggle — SMS (always available) vs RCS (only when
            configured + enabled). */}
        <div className="flex items-center gap-2" data-testid="twilio-send-channel-group">
          {[
            { v: "sms", label: "SMS", icon: MessageSquare },
            { v: "rcs", label: "RCS", icon: Sparkles },
          ].map(({ v, label, icon: Icon }) => {
            const active = channel === v;
            const disabledChannel = v === "rcs" && !canSendRcs;
            return (
              <button key={v} type="button"
                disabled={!configured || disabledChannel}
                onClick={() => setChannel(v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${
                  active
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title={disabledChannel ? "Configure + enable RCS above first" : ""}
                data-testid={`twilio-send-channel-${v}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            );
          })}
          {!canSendRcs && (
            <span className="text-[10px] text-zinc-400 ml-1">
              RCS disabled — configure it above to test.
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
          <div className="space-y-1.5">
            <Label className="label-overline">To (E.164)</Label>
            <Input className="rounded-sm font-mono text-[12px]" placeholder="+61400000000"
              value={to} onChange={(e) => setTo(e.target.value)}
              disabled={!configured}
              data-testid="twilio-send-to" />
          </div>
          <Button type="button" size="sm" className="rounded-sm md:self-end"
            onClick={send} disabled={!configured || sending}
            data-testid="twilio-send-go">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
            Send test
          </Button>
        </div>
        <div className="space-y-1.5">
          <Label className="label-overline">Body</Label>
          <Textarea className="rounded-sm text-[12px]" rows={3} maxLength={1600}
            value={body} onChange={(e) => setBody(e.target.value)}
            disabled={!configured}
            data-testid="twilio-send-body" />
          <p className="text-[10px] text-zinc-500">
            {body.length}/1600 chars · longer messages are split into segments by Twilio.
            {channel === "rcs" && " RCS lifts the SMS length limit (up to ~2KB)."}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function MessagesCard({ rows, onRefreshRow, refreshingSid }) {
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-3">
          <MessageSquare className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">Recent sends</h2>
          <Badge variant="outline" className="rounded-sm text-[10px] ml-2">{rows.length}</Badge>
        </div>
        {rows.length === 0 ? (
          <p className="text-[12px] text-zinc-500 italic">No SMS messages sent yet. Use the panel above to fire a test.</p>
        ) : (
          <div className="rounded-sm border border-zinc-200 divide-y divide-zinc-100 max-h-[420px] overflow-y-auto">
            {rows.map((r) => (
              <div key={r.sid} className="px-3 py-2 text-[12px] grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2"
                data-testid={`twilio-msg-${r.sid}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-zinc-700">{r.to}</span>
                    <Badge className={`rounded-sm text-[10px] border ${STATUS_TONE[r.status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                      {r.status}
                    </Badge>
                    {r.num_segments > 1 && (
                      <Badge variant="outline" className="rounded-sm text-[10px]">{r.num_segments} segs</Badge>
                    )}
                    {r.price && (
                      <span className="text-[10px] text-zinc-500 font-mono">
                        {r.price} {r.price_unit}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-600 mt-0.5 line-clamp-1">{r.body}</div>
                  <div className="text-[10px] text-zinc-400 font-mono mt-0.5">
                    {r.created_at} · {r.sid} · by {r.sent_by || "—"}
                    {r.error_code && <span className="text-rose-600 ml-2">err {r.error_code}: {r.error_message}</span>}
                  </div>
                </div>
                <Button type="button" variant="ghost" size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => onRefreshRow(r.sid)}
                  disabled={refreshingSid === r.sid}
                  title="Refresh status from Twilio"
                  data-testid={`twilio-refresh-${r.sid}`}>
                  {refreshingSid === r.sid
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function TwilioMasterPage() {
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [refreshingSid, setRefreshingSid] = useState(null);

  // Inventory state — phone numbers, messaging services, WhatsApp,
  // balance. Loaded lazily once the page knows credentials are saved.
  const [inventory, setInventory] = useState({
    balance: null, numbers: null, services: null, whatsapp: null,
    loading: false, error: null,
  });

  const loadAll = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/twilio/master"),
        api.get("/twilio/master/messages?limit=50").catch(() => ({ data: [] })),
      ]);
      setState(s.data || {});
      setMessages(m.data || []);
    } catch (e) {
      toast.error("Failed to load Twilio config");
    }
  }, []);

  const loadInventory = useCallback(async () => {
    setInventory((s) => ({ ...s, loading: true, error: null }));
    try {
      const [bal, nums, svcs, wa, rcs] = await Promise.all([
        api.get("/twilio/master/balance").catch(() => ({ data: null })),
        api.get("/twilio/master/numbers").catch((e) => ({ data: null, _err: e })),
        api.get("/twilio/master/messaging-services").catch(() => ({ data: null })),
        api.get("/twilio/master/whatsapp-senders").catch(() => ({ data: null })),
        api.get("/twilio/master/rcs-senders").catch(() => ({ data: null })),
      ]);
      setInventory({
        balance: bal.data,
        numbers: nums.data,
        services: svcs.data,
        whatsapp: wa.data,
        rcs: rcs.data,
        loading: false, error: null,
      });
    } catch (e) {
      setInventory((s) => ({ ...s, loading: false,
        error: e.response?.data?.detail || "Inventory load failed" }));
    }
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await loadAll(); setLoading(false); })();
  }, [loadAll]);

  // Auto-load inventory once we know credentials are configured.
  useEffect(() => {
    if (state.configured) loadInventory();
  }, [state.configured, loadInventory]);

  const onSave = async () => {
    setSaving(true);
    const body = {
      account_sid: state.account_sid || "",
      messaging_service_sid: state.messaging_service_sid || "",
      from_number: state.from_number || "",
      rcs_agent_id: state.rcs_agent_id || "",
      rcs_messaging_service_sid: state.rcs_messaging_service_sid || "",
      rcs_enabled: !!state.rcs_enabled,
      rcs_fallback_sms: state.rcs_fallback_sms !== false,
    };
    // Only send auth_token if the user typed a fresh one.
    if (state.auth_token) body.auth_token = state.auth_token;
    try {
      const { data } = await api.put("/twilio/master", body);
      setState({ ...data, auth_token: "" });
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/twilio/master/test");
      toast.success(`Connected · ${data.friendly_name} (${data.status})`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const onClear = async () => {
    if (!window.confirm("Clear stored Twilio credentials? This will disable SMS sending until you re-enter them.")) return;
    try {
      await api.delete("/twilio/master");
      toast.success("Credentials cleared");
      setState({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    }
  };

  const onRefreshRow = async (sid) => {
    setRefreshingSid(sid);
    try {
      await api.post(`/twilio/master/messages/${encodeURIComponent(sid)}/refresh`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setRefreshingSid(null); }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Twilio…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="twilio-page">
      <div className="pb-4 border-b border-zinc-200">
        <div className="label-overline">Admin · Integrations · Twilio</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
          Twilio SMS
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Account SID + Auth Token from{" "}
          <a className="underline decoration-zinc-400 underline-offset-2"
            href="https://console.twilio.com" target="_blank" rel="noreferrer">console.twilio.com</a>.
          Used for SMS 2FA, client notifications and ad-hoc admin sends.
          See the{" "}
          <a className="underline decoration-zinc-400 underline-offset-2"
            href="https://www.twilio.com/docs/usage/api" target="_blank" rel="noreferrer">API docs</a>{" "}
          for advanced features.
        </p>
      </div>

      <CredsCard state={state} setState={setState} onSave={onSave} onTest={onTest}
        onClear={onClear} saving={saving} testing={testing} />

      <RcsConfigCard state={state} setState={setState} onSave={onSave} saving={saving} />

      {state.configured && (
        <>
          <AccountInventoryCard inv={inventory} onRefresh={loadInventory} />
          <PhoneNumbersCard inv={inventory} onChanged={loadInventory} />
          <MessagingServicesCard inv={inventory} />
          <RcsSendersCard inv={inventory} configuredAgentId={state.rcs_agent_id} />
          <WhatsAppCard inv={inventory} />
        </>
      )}

      <SendTestCard configured={state.configured}
        rcsConfigured={state.rcs_configured}
        rcsEnabled={state.rcs_enabled}
        onSent={loadAll} />

      <MessagesCard rows={messages} onRefreshRow={onRefreshRow} refreshingSid={refreshingSid} />

      <IntegrationCapabilities integrationKey="twilio" accent="#F22F46" />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Account / balance card. Shows live USD balance, configured From
 * number + Messaging Service SID, and the last-verified timestamp.
 * Cheap to refresh — backend just calls Twilio's Balance fetch.
 * ────────────────────────────────────────────────────────────────── */
function AccountInventoryCard({ inv, onRefresh }) {
  const b = inv.balance;
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="twilio-account-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
          <Wallet className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">
            Account &amp; balance
          </h2>
          <Button type="button" variant="ghost" size="icon"
            className="ml-auto h-7 w-7"
            onClick={onRefresh}
            disabled={inv.loading}
            title="Refresh from Twilio"
            data-testid="twilio-inventory-refresh">
            {inv.loading
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <InventoryStat label="Balance"
            value={b ? `${Number(b.balance).toFixed(2)} ${b.currency}` : "—"}
            testid="twilio-balance-value" />
          <InventoryStat label="Numbers"
            value={inv.numbers?.count ?? "—"}
            testid="twilio-numbers-count" />
          <InventoryStat label="Messaging Services"
            value={inv.services?.count ?? "—"}
            testid="twilio-services-count" />
          <InventoryStat label="WhatsApp senders"
            value={inv.whatsapp?.count ?? "—"}
            testid="twilio-whatsapp-count" />
        </div>

        {b?.fetched_at && (
          <div className="text-[10px] font-mono text-zinc-400">
            balance fetched {b.fetched_at.slice(0,19).replace("T", " ")} UTC
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InventoryStat({ label, value, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white" data-testid={testid}>
      <div className="label-overline text-[9px]">{label}</div>
      <div className="font-display font-bold text-xl mt-0.5">{value}</div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Phone numbers card. Lists every IncomingPhoneNumber Twilio reports
 * with editable friendly_name + Voice/SMS webhook URLs. Edits are
 * pushed back via PATCH /master/numbers/{sid}.
 * ────────────────────────────────────────────────────────────────── */
function PhoneNumbersCard({ inv, onChanged }) {
  const { clients } = useClients();
  const [editSid, setEditSid] = useState(null);
  const [draft, setDraft] = useState({});
  const [savingSid, setSavingSid] = useState(null);
  // Local edits to {client_id, description} per SID. Saved on blur /
  // dropdown change so the admin doesn't need a Save button per row.
  const [asgDraft, setAsgDraft] = useState({});  // {sid: {client_id, description}}
  const [asgSaving, setAsgSaving] = useState({});
  const nums = inv.numbers?.numbers || [];

  // Seed asgDraft from the freshly-loaded numbers list.
  useEffect(() => {
    const seed = {};
    for (const n of nums) {
      seed[n.sid] = {
        client_id: n.assigned_client_id || "",
        description: n.description || "",
      };
    }
    setAsgDraft(seed);
  }, [inv.numbers]);

  const saveAssignment = async (sid, partial) => {
    const current = asgDraft[sid] || { client_id: "", description: "" };
    const next = { ...current, ...partial };
    setAsgDraft((d) => ({ ...d, [sid]: next }));
    setAsgSaving((s) => ({ ...s, [sid]: true }));
    try {
      await api.post(`/twilio/master/numbers/${sid}/assignment`, {
        client_id: next.client_id || null,
        description: next.description || "",
      });
      toast.success(next.client_id
        ? "Number assigned"
        : next.description ? "Description saved" : "Cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save assignment");
      // Roll back to previous values from the inv list.
      const n = nums.find((r) => r.sid === sid);
      setAsgDraft((d) => ({
        ...d,
        [sid]: {
          client_id: n?.assigned_client_id || "",
          description: n?.description || "",
        },
      }));
    } finally {
      setAsgSaving((s) => {
        const nx = { ...s };
        delete nx[sid];
        return nx;
      });
    }
  };

  const startEdit = (n) => {
    setEditSid(n.sid);
    setDraft({
      friendly_name: n.friendly_name || "",
      voice_url: n.voice_url || "",
      voice_method: n.voice_method || "POST",
      sms_url: n.sms_url || "",
      sms_method: n.sms_method || "POST",
    });
  };
  const cancelEdit = () => { setEditSid(null); setDraft({}); };
  const saveEdit = async (sid) => {
    setSavingSid(sid);
    try {
      await api.patch(`/twilio/master/numbers/${sid}`, draft);
      toast.success("Number updated");
      cancelEdit();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSavingSid(null); }
  };

  const capPills = (caps = {}) => (
    <div className="flex gap-1 flex-wrap">
      {["voice", "sms", "mms", "fax"].filter((k) => caps[k]).map((k) => (
        <Badge key={k} variant="outline"
          className="rounded-sm text-[9px] font-display font-bold uppercase tracking-[0.15em] bg-zinc-50">
          {k}
        </Badge>
      ))}
    </div>
  );

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="twilio-numbers-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
          <Phone className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">
            Phone numbers
          </h2>
          {inv.numbers && (
            <span className="text-[10px] font-mono text-zinc-500 ml-auto">
              {nums.length} owned
            </span>
          )}
        </div>

        {inv.loading && !inv.numbers ? (
          <div className="py-6 text-center text-xs text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </div>
        ) : nums.length === 0 ? (
          <div className="text-xs italic text-zinc-500 py-4 text-center">
            No phone numbers found on this Twilio account.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {nums.map((n) => (
              <div key={n.sid} data-testid={`twilio-number-${n.sid}`}
                className="py-3 first:pt-0 last:pb-0">
                {editSid !== n.sid ? (
                  <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_140px] gap-3">
                    <div>
                      <div className="font-mono text-sm font-semibold">{n.phone_number}</div>
                      <div className="text-[10px] font-mono text-zinc-400 mt-0.5 truncate" title={n.sid}>
                        {n.sid?.slice(0, 12)}…
                      </div>
                      <div className="mt-1.5">{capPills(n.capabilities)}</div>
                    </div>
                    <div className="text-xs space-y-1">
                      <div>
                        <span className="label-overline text-[9px] mr-1.5">Friendly name</span>
                        {n.friendly_name || <span className="italic text-zinc-400">—</span>}
                      </div>
                      {n.voice_url && (
                        <div className="truncate font-mono text-[11px] text-zinc-600">
                          <span className="label-overline text-[9px] mr-1.5">Voice webhook</span>
                          {n.voice_method || "POST"} {n.voice_url}
                        </div>
                      )}
                      {n.sms_url && (
                        <div className="truncate font-mono text-[11px] text-zinc-600">
                          <span className="label-overline text-[9px] mr-1.5">SMS webhook</span>
                          {n.sms_method || "POST"} {n.sms_url}
                        </div>
                      )}
                      {n.voice_application_sid && (
                        <div className="font-mono text-[10px] text-zinc-500">
                          Voice TwiML App: {n.voice_application_sid}
                        </div>
                      )}
                    </div>
                    <div className="md:text-right">
                      <Button type="button" variant="outline" size="sm"
                        className="rounded-sm" onClick={() => startEdit(n)}
                        data-testid={`twilio-number-edit-${n.sid}`}>
                        <Edit3 className="h-3 w-3 mr-1.5" /> Configure
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 bg-zinc-50/60 -mx-3 px-3 py-3 rounded-sm">
                    <div className="font-mono text-sm font-semibold">{n.phone_number}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <Label className="label-overline text-[9px]">Friendly name</Label>
                        <Input className="rounded-sm h-8 text-xs"
                          value={draft.friendly_name}
                          onChange={(e) => setDraft((d) => ({ ...d, friendly_name: e.target.value }))}
                          data-testid={`twilio-number-name-${n.sid}`} />
                      </div>
                      <div>
                        <Label className="label-overline text-[9px]">Status callback (optional)</Label>
                        <Input className="rounded-sm h-8 text-xs font-mono"
                          placeholder="https://…/twilio/status"
                          value={draft.status_callback || ""}
                          onChange={(e) => setDraft((d) => ({ ...d, status_callback: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="label-overline text-[9px]">Voice webhook</Label>
                        <Input className="rounded-sm h-8 text-xs font-mono"
                          placeholder="https://…/twilio/voice"
                          value={draft.voice_url}
                          onChange={(e) => setDraft((d) => ({ ...d, voice_url: e.target.value }))}
                          data-testid={`twilio-number-voice-${n.sid}`} />
                      </div>
                      <div>
                        <Label className="label-overline text-[9px]">SMS webhook</Label>
                        <Input className="rounded-sm h-8 text-xs font-mono"
                          placeholder="https://…/twilio/sms"
                          value={draft.sms_url}
                          onChange={(e) => setDraft((d) => ({ ...d, sms_url: e.target.value }))}
                          data-testid={`twilio-number-sms-${n.sid}`} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="rounded-sm bg-[#F22F46] hover:bg-[#F22F46]/90"
                        onClick={() => saveEdit(n.sid)}
                        disabled={savingSid === n.sid}
                        data-testid={`twilio-number-save-${n.sid}`}>
                        {savingSid === n.sid
                          ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          : <Save className="h-3.5 w-3.5 mr-1.5" />}
                        Save
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-sm"
                        onClick={cancelEdit} disabled={savingSid === n.sid}>
                        <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Client + description assignment — visible regardless
                    of whether the user is editing webhook fields above.
                    Feeds the per-client `/clients/:id/twilio-phones`
                    tab. */}
                <div className="mt-3 pt-3 border-t border-dashed border-zinc-200 grid grid-cols-1 md:grid-cols-[260px_1fr_24px] gap-3 items-start"
                  data-testid={`twilio-number-asg-${n.sid}`}>
                  <div>
                    <Label className="label-overline text-[9px] flex items-center gap-1">
                      <Briefcase className="h-3 w-3" /> Client
                    </Label>
                    <select
                      value={asgDraft[n.sid]?.client_id ?? ""}
                      onChange={(e) => saveAssignment(n.sid, { client_id: e.target.value })}
                      disabled={!!asgSaving[n.sid]}
                      className="mt-1 h-8 w-full rounded-sm border border-zinc-200 bg-white px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#F22F46]"
                      data-testid={`twilio-number-client-${n.sid}`}
                    >
                      <option value="">— Unassigned —</option>
                      {(clients || []).map((c) => (
                        <option key={c.client_id} value={c.client_id}>
                          {c.company_name || c.client_id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="label-overline text-[9px]">Description of use</Label>
                    <Input
                      className="mt-1 h-8 rounded-sm text-xs"
                      placeholder="e.g. Main inbound voice line · Marketing SMS · 2FA"
                      value={asgDraft[n.sid]?.description ?? ""}
                      onChange={(e) =>
                        setAsgDraft((d) => ({
                          ...d,
                          [n.sid]: { ...(d[n.sid] || {}), description: e.target.value },
                        }))
                      }
                      onBlur={(e) => {
                        const seed = nums.find((r) => r.sid === n.sid)?.description || "";
                        if ((e.target.value || "") !== seed) {
                          saveAssignment(n.sid, { description: e.target.value });
                        }
                      }}
                      data-testid={`twilio-number-desc-${n.sid}`}
                    />
                  </div>
                  <div className="md:pt-5 flex items-center justify-center">
                    {asgSaving[n.sid] && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Messaging Services card. Lists Twilio Messaging Services (sender
 * pools) and the phone numbers attached to each, plus a US A2P
 * registration flag for compliance visibility.
 * ────────────────────────────────────────────────────────────────── */
function MessagingServicesCard({ inv }) {
  const svcs = inv.services?.services || [];
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="twilio-services-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
          <Building2 className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">Messaging Services</h2>
          {inv.services && (
            <span className="text-[10px] font-mono text-zinc-500 ml-auto">
              {svcs.length} services
            </span>
          )}
        </div>
        {inv.loading && !inv.services ? (
          <div className="py-6 text-center text-xs text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </div>
        ) : svcs.length === 0 ? (
          <div className="text-xs italic text-zinc-500 py-4 text-center">
            No Messaging Services configured.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {svcs.map((s) => (
              <div key={s.sid} className="py-3 first:pt-0 last:pb-0"
                data-testid={`twilio-service-${s.sid}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="text-sm font-semibold">{s.friendly_name}</div>
                    <div className="text-[10px] font-mono text-zinc-400 mt-0.5">{s.sid}</div>
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      {s.use_case && (
                        <Badge variant="outline" className="rounded-sm text-[9px]">
                          {s.use_case}
                        </Badge>
                      )}
                      <Badge variant="outline"
                        className={`rounded-sm text-[9px] ${s.us_app_to_person_registered ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-zinc-50"}`}>
                        US A2P: {s.us_app_to_person_registered ? "registered" : "not registered"}
                      </Badge>
                    </div>
                  </div>
                  <div className="min-w-[200px]">
                    <div className="label-overline text-[9px] mb-1">Attached numbers · {s.phone_number_count}</div>
                    {(s.phone_numbers || []).length === 0 ? (
                      <div className="text-[10px] italic text-zinc-400">No attached numbers.</div>
                    ) : (
                      <ul className="space-y-0.5">
                        {s.phone_numbers.map((pn) => (
                          <li key={pn.sid} className="text-[11px] font-mono">{pn.phone_number}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                {s.inbound_request_url && (
                  <div className="font-mono text-[10px] text-zinc-500 mt-1.5 truncate"
                    title={s.inbound_request_url}>
                    inbound: {s.inbound_request_url}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * WhatsApp senders card. Empty for accounts using only the
 * sandbox shared number (+14155238886). When a production WhatsApp
 * Business sender is approved it shows up here with its profile
 * name + status (online / pending / offline).
 * ────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────
 * RCS Senders card — RCS Agents registered with this Twilio account.
 * Each carrier-approved sender produces an agent_id that's pasted
 * into the RCS config card above. Statuses can be: ONLINE (ready),
 * PENDING (waiting on carrier approval), or OFFLINE.
 * ────────────────────────────────────────────────────────────────── */
function RcsSendersCard({ inv, configuredAgentId }) {
  const senders = inv.rcs?.senders || [];
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="twilio-rcs-senders-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
          <Sparkles className="h-4 w-4 text-zinc-700" />
          <h2 className="font-display font-bold text-lg tracking-tight">RCS Senders</h2>
          {inv.rcs && (
            <span className="text-[10px] font-mono text-zinc-500 ml-auto"
              data-testid="twilio-rcs-senders-count">
              {senders.length} senders
            </span>
          )}
        </div>
        {inv.loading && !inv.rcs ? (
          <div className="py-6 text-center text-xs text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </div>
        ) : senders.length === 0 ? (
          <div className="text-xs italic text-zinc-500 py-4">
            No RCS senders registered on this account yet. Register one
            via{" "}
            <a className="underline decoration-zinc-400"
              href="https://console.twilio.com/us1/develop/sms/senders/rcs-senders"
              target="_blank" rel="noreferrer">
              console.twilio.com → Messaging → RCS → Senders
            </a>{" "}
            and submit it for carrier approval — then paste the resulting
            agent ID into the RCS config card above.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {senders.map((s) => {
              const isActive = (configuredAgentId || "").trim() === (s.agent_id || "");
              const statusTone = (s.status || "").toLowerCase() === "online"
                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : (s.status || "").toLowerCase().includes("pend")
                  ? "bg-amber-100 text-amber-800 border-amber-200"
                  : "bg-zinc-100 text-zinc-700 border-zinc-200";
              return (
                <div key={s.sid || s.agent_id} className="py-3 first:pt-0 last:pb-0 flex items-start gap-3 flex-wrap"
                  data-testid={`twilio-rcs-sender-${s.agent_id || s.sid}`}>
                  {s.logo_url && (
                    <img src={s.logo_url} alt=""
                      className="h-10 w-10 rounded-sm object-cover border border-zinc-200 bg-white" />
                  )}
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold">
                        {s.display_name || s.agent_id}
                      </div>
                      <Badge className={`rounded-sm text-[9px] ${statusTone}`}>
                        {s.status || "unknown"}
                      </Badge>
                      {isActive && (
                        <Badge className="rounded-sm text-[9px] bg-sky-100 text-sky-800 border-sky-200">
                          configured
                        </Badge>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                      {s.agent_id}
                    </div>
                    {s.description && (
                      <div className="text-[11px] text-zinc-600 mt-1 max-w-2xl">
                        {s.description}
                      </div>
                    )}
                  </div>
                  {s.webhook_url && (
                    <div className="font-mono text-[10px] text-zinc-500 max-w-xs truncate"
                      title={s.webhook_url}>
                      webhook: {s.webhook_url}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function WhatsAppCard({ inv }) {
  const senders = inv.whatsapp?.senders || [];
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="twilio-whatsapp-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
          <MessageCircle className="h-4 w-4 text-[#25D366]" />
          <h2 className="font-display font-bold text-lg tracking-tight">WhatsApp senders</h2>
          {inv.whatsapp && (
            <span className="text-[10px] font-mono text-zinc-500 ml-auto">
              {senders.length} senders
            </span>
          )}
        </div>
        {inv.loading && !inv.whatsapp ? (
          <div className="py-6 text-center text-xs text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </div>
        ) : senders.length === 0 ? (
          <div className="text-xs text-zinc-600 leading-relaxed">
            No production WhatsApp Business senders are bound to this account.
            If you&apos;re using the
            <span className="font-mono mx-1">whatsapp:+14155238886</span>
            sandbox number, it&apos;s a shared resource and won&apos;t appear in any
            inventory API. To register a dedicated WhatsApp Business sender,
            visit
            <a href="https://console.twilio.com/us1/develop/sms/senders/whatsapp-senders"
              target="_blank" rel="noreferrer"
              className="text-[#F22F46] underline mx-1 inline-flex items-center gap-1">
              Twilio WhatsApp Senders <Globe className="h-3 w-3" />
            </a>
            and complete Meta business verification.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {senders.map((s, idx) => (
              <div key={s.sid || idx} className="py-2 first:pt-0 last:pb-0 text-sm flex items-center gap-2"
                data-testid={`twilio-whatsapp-${idx}`}>
                <span className="font-mono">{s.sender_id || s.sid}</span>
                <Badge variant="outline" className="rounded-sm text-[9px]">
                  {s.status || "—"}
                </Badge>
                {s.profile && (
                  <span className="text-[11px] text-zinc-500">{s.profile}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
